import { z } from "zod"

/** S1 wire contract only. Registering hub methods and background refresh belongs to S2/S3/R1. */
export const benefitRealtimeLimits = {
  schemaVersion: 1,
  maxSubscriptionsPerConnection: 32,
  leaseSeconds: 120,
  renewAfterSeconds: 60,
  revalidateEverySeconds: 30,
  maxMessageBytes: 4096,
} as const

// .NET Guid's D representation, not an RFC UUID version restriction. Synthetic wire fixtures
// must use actual non-empty GUIDs too. Do not accept paths, Guid.Empty or arbitrary fixture names.
export const benefitGuidSchema = z.string().regex(/^(?!00000000-0000-0000-0000-000000000000$)[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/)
const scopeKey = z.string().regex(/^[0-9A-F]{64}$/)
const version = z.literal(benefitRealtimeLimits.schemaVersion)
export const benefitAspects = ["facts", "income", "workflow", "ownership", "grant-review", "delivery", "history"] as const
const resource = z.strictObject({ kind: z.literal("benefit-application"), id: benefitGuidSchema })
const collection = z.strictObject({ kind: z.literal("benefit-collection"), topic: z.enum(["applications", "catalogs", "people"]) })
const target = z.union([resource, collection])
const opaqueHint = z.string().min(1).max(256)

export const resourceChangedSchema = z.strictObject({
  schemaVersion: version,
  eventId: benefitGuidSchema,
  resource,
  change: z.enum(["updated", "deleted", "access-changed"]),
  aspects: z.array(z.enum(benefitAspects)).min(1).max(benefitAspects.length),
  revisionHint: opaqueHint.optional(),
  correlationId: opaqueHint.optional(),
})

export const benefitRealtimeSchemas = {
  subscribe: z.strictObject({ schemaVersion: version, requestId: benefitGuidSchema, scopeKey, target }),
  subscribed: z.strictObject({
    schemaVersion: version, requestId: benefitGuidSchema, scopeKey, target,
    subscriptionId: benefitGuidSchema, leaseSeconds: z.literal(benefitRealtimeLimits.leaseSeconds),
  }),
  refused: z.strictObject({
    schemaVersion: version, requestId: benefitGuidSchema,
    code: z.enum(["invalid-request", "unavailable", "scope-changed", "limit-exceeded", "temporarily-unavailable"]),
  }),
  unsubscribe: z.strictObject({ schemaVersion: version, requestId: benefitGuidSchema, subscriptionId: benefitGuidSchema }),
  unsubscribed: z.strictObject({ schemaVersion: version, requestId: benefitGuidSchema, subscriptionId: benefitGuidSchema }),
  revoked: z.strictObject({
    schemaVersion: version, eventId: benefitGuidSchema, subscriptionId: benefitGuidSchema,
    code: z.enum(["access-changed", "expired"]),
  }),
  resourceChanged: resourceChangedSchema,
  collectionChanged: z.strictObject({ schemaVersion: version, eventId: benefitGuidSchema, collection }),
} as const

export type BenefitSubscriptionRequest = z.infer<typeof benefitRealtimeSchemas.subscribe>
export type BenefitSubscriptionAcknowledgment = z.infer<typeof benefitRealtimeSchemas.subscribed>
export type BenefitCollectionChanged = z.infer<typeof benefitRealtimeSchemas.collectionChanged>
export type ResourceChangedV1 = z.infer<typeof resourceChangedSchema>

/** Language-neutral schemas for the server implementation and its shared valid/invalid corpus. */
export function benefitRealtimeJsonSchema() {
  return {
    $schema: "https://json-schema.org/draft/2020-12/schema",
    title: "CMS benefit realtime v1",
    description: "Contract definitions, not authorization. See docs/benefit-realtime-contract.md.",
    $defs: Object.fromEntries(Object.entries(benefitRealtimeSchemas).map(([name, schema]) => {
      const { $schema: _dialect, ...definition } = z.toJSONSchema(schema)
      return [name, definition]
    })),
  }
}
